<template>
  <a-spin :spinning="loading">
    <a-tabs v-model:activeKey="activeSettingsTab" :animated="false">
      <a-tab-pane key="vm-configuration" :tab="$t('label.workspace.vm.configuration')" />
      <a-tab-pane key="security" :tab="$t('label.workspace.security')" />
      <a-tab-pane key="sync" :tab="$t('label.workspace.sync')" />
    </a-tabs>
    <a-card v-if="activeSettingsTab === 'vm-configuration'" :title="$t('label.workspace.settings.defaults')">
      <a-alert type="info" show-icon style="margin-bottom: 16px" :message="$t('message.workspace.settings.defaults')" />

      <a-form layout="vertical">
        <a-form-item :label="$t('label.workspace.vdi.compute.offering')">
          <a-select
            v-model:value="serviceOfferingId"
            show-search
            optionFilterProp="label"
            :options="serviceOfferingOptions"
            :disabled="savingDefaults || !canUpdateDefaults"
          />
        </a-form-item>
        <a-form-item :label="$t('label.workspace.vdi.image')">
          <a-select
            v-model:value="templateId"
            show-search
            optionFilterProp="label"
            :options="templateOptions"
            :disabled="savingDefaults || !canUpdateDefaults"
          />
        </a-form-item>

        <a-space>
        <a-form-item :label="$t('label.workspace.minimum.disk.size')">
          <a-input-number
            v-model:value="minimumDiskSize"
            :min="1"
            :precision="0"
            :disabled="savingDefaults || !canUpdateConfiguration"
            style="width: 100%" />
          <div class="form-help">{{ $t('message.workspace.minimum.disk.size') }}</div>
        </a-form-item>
          <a-button type="primary" :loading="savingDefaults" :disabled="!canSaveDefaults" @click="saveDefaults">
            {{ $t('label.save') }}
          </a-button>
          <a-button :disabled="savingDefaults || !defaultsChanged" @click="resetDefaults">
            {{ $t('label.reset') }}
          </a-button>
        </a-space>
      </a-form>
    </a-card>

    <a-card v-if="activeSettingsTab === 'security' && isHorizon" :title="$t('label.workspace.broker.migration')" style="margin-top: 16px">
      <a-row type="flex" justify="space-between" align="middle" :gutter="16">
        <a-col :xs="24" :md="14">
          <p>{{ $t('message.workspace.broker.migration.horizon.to.smartrtc') }}</p>
        </a-col>
        <a-col :xs="24" :md="10" class="integration-actions">
          <a-button
            type="primary"
            danger
            :loading="runningAction === 'migrate-broker-smartrtc'"
            :disabled="integrationRunning || !canMigrateBroker"
            @click="confirmBrokerMigrationToSmartRtc">
            {{ $t('label.workspace.broker.migrate.to.smartrtc') }}
          </a-button>
        </a-col>
      </a-row>
      <div class="form-help integration-help">{{ $t('message.workspace.broker.migration.horizon.to.smartrtc.lifecycle') }}</div>
    </a-card>

    <template v-if="activeSettingsTab === 'security' && isHorizon">
      <a-card :title="$t('label.workspace.duo')" style="margin-top: 16px">
        <a-row type="flex" justify="space-between" align="middle" :gutter="16">
          <a-col :xs="24" :md="14">
            <p>{{ $t('message.workspace.duo') }}</p>
            <a-tag :color="duoStatusColor">{{ duoStatusLabel }}</a-tag>
          </a-col>
          <a-col :xs="24" :md="10" class="integration-actions">
            <a-space v-if="!duoConfigured">
              <a-button
                type="primary"
                :disabled="integrationRunning || !canConfigureDuo || showDuoForm"
                @click="showDuoForm = true"
              >
                {{ $t('label.workspace.duo.configure') }}
              </a-button>
            </a-space>
            <a-space v-else>
              <a-button
                :type="duoPaused ? 'primary' : 'default'"
                :loading="runningAction === (duoPaused ? 'unpause-duo' : 'pause-duo')"
                :disabled="integrationRunning || !canConfigureDuo"
                @click="runDuoAction(duoPaused ? 'unpause' : 'pause')"
              >
                {{ duoPaused ? $t('label.workspace.duo.unpause') : $t('label.workspace.duo.pause') }}
              </a-button>
              <a-button
                danger
                :loading="runningAction === 'disable-duo'"
                :disabled="integrationRunning || !canConfigureDuo"
                @click="runDuoAction('disable')"
              >
                {{ $t('label.workspace.duo.disable') }}
              </a-button>
            </a-space>
          </a-col>
        </a-row>

        <a-alert
          v-if="!canConfigureDuo"
          type="warning"
          show-icon
          style="margin-top: 16px"
          :message="$t('message.workspace.integration.contact')"
        />

        <div class="form-help integration-help">{{ $t('message.workspace.duo.lifecycle') }}</div>
      </a-card>
    </template>

    <a-card v-if="activeSettingsTab === 'security' && !isHorizon" :title="$t('label.workspace.broker.migration')" style="margin-top: 16px">
      <a-row type="flex" justify="space-between" align="middle" :gutter="16">
        <a-col :xs="24" :md="14">
          <p>{{ $t('message.workspace.broker.migration.smartrtc.to.horizon') }}</p>
        </a-col>
        <a-col :xs="24" :md="10" class="integration-actions">
          <a-button
            type="primary"
            danger
            :loading="runningAction === 'migrate-broker-horizon'"
            :disabled="integrationRunning || !canMigrateBroker"
            @click="confirmBrokerMigration">
            {{ $t('label.workspace.broker.migrate.to.horizon') }}
          </a-button>
        </a-col>
      </a-row>
      <div class="form-help integration-help">{{ $t('message.workspace.broker.migration.smartrtc.to.horizon.lifecycle') }}</div>
    </a-card>

    <a-card v-if="activeSettingsTab === 'security' && !isHorizon" :title="$t('label.workspace.two.factor')" style="margin-top: 16px">
      <a-row type="flex" justify="space-between" align="middle" :gutter="16">
        <a-col :xs="24" :md="14">
          <p>{{ $t('message.workspace.two.factor') }}</p>
        </a-col>
        <a-col :xs="24" :md="10" class="integration-actions">
          <a-button
            type="primary"
            :loading="runningAction === 'toggle-totp'"
            :disabled="integrationRunning || !canToggleTwoFactor"
            @click="toggleTwoFactor">
            {{ $t('label.workspace.toggle.totp') }}
          </a-button>
        </a-col>
      </a-row>
    </a-card>

    <a-card v-if="activeSettingsTab === 'sync'" :title="$t('label.workspace.cloud.sync')" style="margin-top: 16px">
      <a-row type="flex" justify="space-between" align="middle" :gutter="16">
        <a-col :xs="24" :md="14">
          <p>{{ cloudSyncDescription }}</p>
          <a-tag :color="cloudSyncStatusColor">{{ cloudSyncStatusLabel }}</a-tag>
        </a-col>
        <a-col v-if="cloudSyncEnabled" :xs="24" :md="10" class="integration-actions">
          <a-space>
            <a-button
              :type="cloudSyncPaused ? 'primary' : 'default'"
              :loading="runningAction === (cloudSyncPaused ? 'unpause-cloud-sync' : 'pause-cloud-sync')"
              :disabled="integrationRunning || !canChangeCloudSyncState"
              @click="runCloudSyncAction(cloudSyncPaused ? 'unpause' : 'pause')"
            >
              {{ cloudSyncPaused ? $t('label.workspace.cloud.sync.unpause') : $t('label.workspace.cloud.sync.pause') }}
            </a-button>
            <a-button
              danger
              :loading="runningAction === 'disable-cloud-sync'"
              :disabled="integrationRunning || !canDisableCloudSync"
              @click="confirmCloudSyncDisable"
            >
              {{ $t('label.workspace.cloud.sync.disable') }}
            </a-button>
          </a-space>
        </a-col>
      </a-row>

      <a-alert
        v-if="cloudSyncEnabled && !canManageCloudSync"
        type="warning"
        show-icon
        style="margin-top: 16px"
        :message="$t('message.workspace.cloud.sync.permission')"
      />
      <a-alert
        v-else-if="cloudSyncEnabled && !hasCloudSyncConfiguration"
        type="warning"
        show-icon
        style="margin-top: 16px"
        :message="$t('message.workspace.cloud.sync.incomplete')"
      />
    </a-card>

    <a-card v-if="activeSettingsTab === 'sync'" :title="$t('label.workspace.entra.sync')" style="margin-top: 16px">
        <a-row type="flex" justify="space-between" align="middle" :gutter="16">
          <a-col :xs="24" :md="14">
            <p>{{ entraDescription }}</p>
            <a-tag :color="entraStatusColor">{{ entraStatusLabel }}</a-tag>
          </a-col>
          <a-col :xs="24" :md="10" class="integration-actions">
            <a-button
              v-if="!entraInstalled"
              type="primary"
              :loading="runningAction === 'install-entra'"
              :disabled="integrationRunning || !canInstallEntra"
              @click="confirmEntraInstall"
            >
              {{ $t('label.workspace.entra.install') }}
            </a-button>
            <a-button
              v-else-if="entraConfigured"
              :type="entraPaused ? 'primary' : 'default'"
              :loading="runningAction === (entraPaused ? 'unpause-entra' : 'pause-entra')"
              :disabled="integrationRunning || !canManageEntra"
              @click="runEntraAction(entraPaused ? 'unpause' : 'pause')"
            >
              {{ entraPaused ? $t('label.workspace.entra.unpause') : $t('label.workspace.entra.pause') }}
            </a-button>
          </a-col>
        </a-row>

        <a-alert
          v-if="!entraInstalled && !canInstallEntra"
          type="warning"
          show-icon
          style="margin-top: 16px"
          :message="$t('message.workspace.integration.contact')"
        />
    </a-card>

    <a-modal
      :title="$t('label.workspace.duo.configure')"
      :visible="showDuoForm"
      :confirm-loading="runningAction === 'configure-duo'"
      :ok-button-props="{ disabled: integrationRunning || !duoFormValid }"
      :cancel-button-props="{ disabled: integrationRunning }"
      :ok-text="$t('label.workspace.duo.deploy')"
      :cancel-text="$t('label.cancel')"
      @ok="configureDuo"
      @cancel="cancelDuoConfiguration"
    >
      <a-form layout="vertical" @submit.prevent="configureDuo">
        <a-alert
          type="info"
          show-icon
          style="margin-bottom: 16px"
          :message="$t('message.workspace.duo.credentials')"
        />
        <a-form-item :label="$t('label.workspace.duo.integration.key')" required>
          <a-input v-model:value="duoIntegrationKey" autocomplete="off" placeholder="DIXXXXXXXXXXXXXXXXXX" />
        </a-form-item>
        <a-form-item :label="$t('label.workspace.duo.secret.key')" required>
          <a-input-password v-model:value="duoSecretKey" autocomplete="new-password" />
        </a-form-item>
        <a-form-item :label="$t('label.workspace.duo.api.host')" required>
          <a-input v-model:value="duoApiHost" autocomplete="off" placeholder="api-XXXXXXXX.duosecurity.com" />
          <div class="form-help">{{ $t('message.workspace.duo.api.host') }}</div>
        </a-form-item>
      </a-form>
    </a-modal>
  </a-spin>
</template>

<script>
import { templateMatchesOffering, listAllEntries } from '@/utils/hostTags'
import AnsibleMixin from './AnsibleMixin'

const asBoolean = (value) => value === true || value === 'true' || value === 1 || value === '1'

export default {
  name: 'WorkspaceSettings',
  mixins: [AnsibleMixin],
  props: {
    workspaceId: { type: String, required: true }
  },
  data () {
    return {
      workspace: {},
      templates: [],
      serviceOfferings: [],
      templateId: null,
      serviceOfferingId: null,
      savedTemplateId: null,
      savedServiceOfferingId: null,
      loading: true,
      activeSettingsTab: 'vm-configuration',
      minimumDiskSize: 100,
      savedMinimumDiskSize: 100,
      savingDefaults: false,
      runningAction: null,
      duoConfigured: false,
      duoPaused: false,
      showDuoForm: false,
      duoIntegrationKey: '',
      duoSecretKey: '',
      duoApiHost: '',
      entraInstalled: false,
      entraConfigured: false,
      entraPaused: false,
      cloudSyncEnabled: false,
      cloudSyncPaused: false,
      cloudSyncAzureDomain: '',
      cloudSyncCredentialId: '',
      cloudSyncServicePrincipalId: '',
      cloudSyncProvisioningJobId: '',
      cloudSyncPasswordHashJobId: ''
    }
  },
  computed: {
    availableApis () {
      return this.$store.getters.apis || {}
    },
    canUpdateDefaults () {
      return 'updateWorkspace' in this.availableApis
    },
    canUpdateConfiguration () {
      return 'updateWorkspaceConfiguration' in this.availableApis
    },
    canToggleTwoFactor () {
      return 'authorizeWorkspaceTwoFactorUpdate' in this.availableApis && 'startAnsibleTaskAsync' in this.availableApis
    },
    canMigrateBroker () {
      return (
        this.workspace &&
        ['smartrtc', 'horizon'].includes(this.workspace.brokerType) &&
        'authorizeWorkspaceBrokerMigration' in this.availableApis &&
        'startAnsibleTaskAsync' in this.availableApis
      )
    },
    canConfigureDuo () {
      return 'authorizeWorkspaceDuoConfiguration' in this.availableApis
    },
    canInstallEntra () {
      return 'authorizeWorkspaceEntraSyncInstallation' in this.availableApis
    },
    canManageEntra () {
      return 'authorizeWorkspaceEntraSyncManagement' in this.availableApis
    },
    canManageCloudSync () {
      return (
        'authorizeWorkspaceCloudSyncManagement' in this.availableApis &&
        'startAnsibleTaskAsync' in this.availableApis &&
        'getWorkspaceConfiguration' in this.availableApis
      )
    },
    hasCloudSyncConfiguration () {
      return Boolean(
        this.cloudSyncCredentialId &&
        this.cloudSyncServicePrincipalId &&
        this.cloudSyncProvisioningJobId &&
        this.cloudSyncPasswordHashJobId
      )
    },
    canChangeCloudSyncState () {
      return this.cloudSyncEnabled && this.canManageCloudSync && this.hasCloudSyncConfiguration
    },
    canDisableCloudSync () {
      return Boolean(
        this.cloudSyncEnabled &&
        this.canManageCloudSync &&
        this.cloudSyncCredentialId &&
        this.cloudSyncServicePrincipalId
      )
    },
    isHorizon () {
      return this.workspace.brokerType === 'horizon'
    },
    integrationRunning () {
      return Boolean(this.runningAction)
    },
    defaultsChanged () {
      return (
        this.templateId !== this.savedTemplateId ||
        this.serviceOfferingId !== this.savedServiceOfferingId ||
        this.minimumDiskSize !== this.savedMinimumDiskSize
      )
    },
    canSaveDefaults () {
      return (
        this.canUpdateDefaults &&
        this.canUpdateConfiguration &&
        this.defaultsChanged &&
        this.templateId &&
        this.serviceOfferingId &&
        Number.isInteger(this.minimumDiskSize) &&
        this.minimumDiskSize >= 1 &&
        !this.savingDefaults
      )
    },
    templateOptions () {
      const offering = this.serviceOfferings.find(offering => offering.id === this.serviceOfferingId)
      return this.templates.filter(template => template.ostypename?.includes('Windows') && templateMatchesOffering(template, offering))
        .map(template => ({ label: template.name, value: template.id }))
    },
    serviceOfferingOptions () {
      return this.serviceOfferings.map((offering) => ({
        label: `${offering.name} - ${offering.cpunumber}vCPU & ${this.humanReadableMemory(offering.memory)} RAM`,
        value: offering.id
      }))
    },
    duoFormValid () {
      return (
        this.duoIntegrationKey.trim().length > 0 &&
        this.duoSecretKey.trim().length > 0 &&
        /^api-[A-Za-z0-9.-]+\.duosecurity\.com$/.test(this.duoApiHost.trim())
      )
    },
    duoStatusColor () {
      return !this.duoConfigured ? 'default' : this.duoPaused ? 'orange' : 'green'
    },
    duoStatusLabel () {
      return !this.duoConfigured
        ? this.$t('label.workspace.integration.disabled')
        : this.duoPaused
          ? this.$t('label.workspace.integration.paused')
          : this.$t('label.workspace.integration.enabled')
    },
    entraStatusColor () {
      return !this.entraInstalled ? 'default' : !this.entraConfigured || this.entraPaused ? 'orange' : 'green'
    },
    entraStatusLabel () {
      if (!this.entraInstalled) return this.$t('label.workspace.entra.not.installed')
      if (!this.entraConfigured) return this.$t('label.workspace.entra.configuration.required')
      return this.entraPaused
        ? this.$t('label.workspace.integration.paused')
        : this.$t('label.workspace.integration.running')
    },
    entraDescription () {
      if (!this.entraInstalled) return this.$t('message.workspace.entra.not.installed')
      if (!this.entraConfigured) return this.$t('message.workspace.entra.not.configured')
      return this.$t('message.workspace.entra.installed')
    },
    cloudSyncStatusColor () {
      return !this.cloudSyncEnabled ? 'default' : this.cloudSyncPaused ? 'orange' : 'green'
    },
    cloudSyncStatusLabel () {
      if (!this.cloudSyncEnabled) return this.$t('label.workspace.cloud.sync.not.enabled')
      return this.cloudSyncPaused
        ? this.$t('label.workspace.integration.paused')
        : this.$t('label.workspace.integration.running')
    },
    cloudSyncDescription () {
      if (!this.cloudSyncEnabled) return this.$t('message.workspace.cloud.sync.not.enabled')
      return this.$t('message.workspace.cloud.sync.enabled', {
        domain: this.cloudSyncAzureDomain || this.$t('label.unknown')
      })
    }
  },
  watch: {
    templateOptions (options) {
      if (!options.some(option => option.value === this.templateId)) this.templateId = undefined
    }
  },
  created () {
    this.load()
  },
  methods: {
    humanReadableMemory (megabytes) {
      if (megabytes >= 1024) return `${Math.round(megabytes / 102.4) / 10}GB`
      return `${megabytes}MB`
    },
    extraFields (appliance) {
      const fields = appliance?.extraFields || appliance?.extrafields || {}
      if (typeof fields !== 'string') return fields
      try {
        return JSON.parse(fields)
      } catch (_) {
        return {}
      }
    },
    async load () {
      this.loading = true
      try {
        const workspaceJson = await this.asyncApi('getWorkspace', { uuid: this.workspaceId })
        this.workspace = workspaceJson.workspaceresponse.payload.workspaces[0]
        this.templateId = this.workspace.vdiTemplateId
        this.serviceOfferingId = this.workspace.vdiServiceOfferingId
        this.savedTemplateId = this.templateId
        this.savedServiceOfferingId = this.serviceOfferingId

        const [templatesJson, offeringsJson, appliancesJson, configurationJson] = await Promise.all([
          listAllEntries('listTemplates', {
            listall: true,
            details: 'all',
            templatefilter: 'executable',
            zoneid: this.workspace.zoneId,
            isready: true
          }, 'listtemplatesresponse', 'template'),
          listAllEntries('listServiceOfferings', { listall: true, zoneid: this.workspace.zoneId, issystem: false }, 'listserviceofferingsresponse', 'serviceoffering'),
          this.asyncApi('listWorkspaceAppliances', { workspace_uuid: this.workspace.uuid }),
          this.asyncApi('listWorkspaceConfigurations', { workspace_uuid: this.workspace.uuid })
        ])
        this.templates = templatesJson
        this.serviceOfferings = offeringsJson

        const configuration = configurationJson.workspaceconfigurationsettingsresponse.payload
        this.minimumDiskSize = configuration.minimumdisksize || 100
        this.savedMinimumDiskSize = this.minimumDiskSize
        const appliances = appliancesJson.workspaceappliancelistresponse.payload.appliances || []
        const brokerFields = this.extraFields(appliances.find((appliance) => appliance.type === 'broker'))
        const dcFields = this.extraFields(appliances.find((appliance) => appliance.type === 'ad-server'))
        const legacyDuoEnabled = asBoolean(brokerFields.duoEnabled)
        this.duoConfigured =
          brokerFields.duoConfigured === undefined ? legacyDuoEnabled : asBoolean(brokerFields.duoConfigured)
        this.duoPaused = asBoolean(brokerFields.duoPaused)
        this.entraInstalled = asBoolean(dcFields.entraSyncInstalled)
        this.entraConfigured = asBoolean(dcFields.entraSyncConfigured)
        this.entraPaused = asBoolean(dcFields.entraSyncPaused)
        this.cloudSyncEnabled = asBoolean(dcFields.entraCloudSyncEnabled)
        this.cloudSyncPaused = asBoolean(dcFields.entraCloudSyncPaused)
        this.cloudSyncAzureDomain = dcFields.entraCloudSyncAzureDomain || ''
        this.cloudSyncCredentialId = dcFields.entraCloudSyncCredentialId || ''
        this.cloudSyncServicePrincipalId = dcFields.entraCloudSyncServicePrincipalId || ''
        this.cloudSyncProvisioningJobId = dcFields.entraCloudSyncProvisioningJobId || ''
        this.cloudSyncPasswordHashJobId = dcFields.entraCloudSyncPasswordHashJobId || ''
      } catch (error) {
        this.$notification.error({
          message: this.$t('label.error'),
          description: this.$t('message.workspace.settings.load.failed')
        })
      } finally {
        this.loading = false
      }
    },
    resetDefaults () {
      this.templateId = this.savedTemplateId
      this.serviceOfferingId = this.savedServiceOfferingId
      this.minimumDiskSize = this.savedMinimumDiskSize
    },
    async saveDefaults () {
      this.savingDefaults = true
      try {
        await Promise.all([
          this.asyncApi('updateWorkspace', {
            uuid: this.workspace.uuid,
            vdi_template_id: this.templateId,
            vdi_service_offering_id: this.serviceOfferingId
          }),
          this.asyncApi('updateWorkspaceConfiguration', {
            workspace_uuid: this.workspace.uuid,
            minimum_disk_size: this.minimumDiskSize
          })
        ])
        this.savedTemplateId = this.templateId
        this.savedServiceOfferingId = this.serviceOfferingId
        this.savedMinimumDiskSize = this.minimumDiskSize
        this.workspace.vdiTemplateId = this.templateId
        this.workspace.vdiServiceOfferingId = this.serviceOfferingId
        this.$notification.success({
          message: this.$t('label.success'),
          description: this.$t('message.workspace.settings.saved')
        })
      } catch (error) {
        this.$notification.error({
          message: this.$t('label.error'),
          description: this.$t('message.workspace.settings.save.failed')
        })
      } finally {
        this.savingDefaults = false
      }
    },
    cancelDuoConfiguration () {
      this.showDuoForm = false
      this.duoIntegrationKey = ''
      this.duoSecretKey = ''
      this.duoApiHost = ''
    },
    async configureDuo () {
      if (!this.duoFormValid) return
      const succeeded = await this.runDuoAction('configure', {
        integrationKey: this.duoIntegrationKey.trim(),
        secretKey: this.duoSecretKey.trim(),
        apiHost: this.duoApiHost.trim().toLowerCase()
      })
      if (succeeded) this.cancelDuoConfiguration()
    },
    async runDuoAction (action, configuration) {
      const variables = { 'workspace.duo.action': action }
      if (configuration) {
        variables['workspace.duo.integration.key'] = configuration.integrationKey
        variables['workspace.duo.secret.key'] = configuration.secretKey
        variables['workspace.duo.api.host'] = configuration.apiHost
      }
      return this.runIntegrationAction({
        actionName: `${action}-duo`,
        playbook: 'configure-workspace-duo',
        integration: this.$t('label.workspace.duo'),
        variables
      })
    },
    confirmEntraInstall () {
      this.$confirm({
        title: this.$t('label.workspace.entra.install.confirm'),
        content: this.$t('message.workspace.entra.install.confirm'),
        okText: this.$t('label.workspace.entra.install.reboot'),
        cancelText: this.$t('label.cancel'),
        onOk: () => this.runEntraAction('install')
      })
    },
    async runEntraAction (action) {
      return this.runIntegrationAction({
        actionName: `${action}-entra`,
        playbook: 'manage-workspace-entra-sync',
        integration: this.$t('label.workspace.entra.sync'),
        variables: { 'workspace.entra.sync.action': action }
      })
    },
    confirmCloudSyncDisable () {
      this.$confirm({
        title: this.$t('label.workspace.cloud.sync.disable.confirm'),
        content: this.$t('message.workspace.cloud.sync.disable.confirm'),
        okText: this.$t('label.workspace.cloud.sync.disable'),
        okType: 'danger',
        cancelText: this.$t('label.cancel'),
        onOk: () => this.runCloudSyncAction('disable')
      })
    },
    async runCloudSyncAction (action) {
      if (action === 'disable' ? !this.canDisableCloudSync : !this.canChangeCloudSyncState) return false
      return this.runIntegrationAction({
        actionName: `${action}-cloud-sync`,
        playbook: 'manage-workspace-cloud-sync',
        integration: this.$t('label.workspace.cloud.sync'),
        variables: {
          'workspace.cloud.sync.action': action,
          'reporting.api.url': `${window.location.origin}/report-api`,
          'reporting.credential.id': this.cloudSyncCredentialId,
          'workspace.cloud.sync.service.principal.id': this.cloudSyncServicePrincipalId,
          'workspace.cloud.sync.provisioning.job.id': this.cloudSyncProvisioningJobId,
          'workspace.cloud.sync.password.hash.job.id': this.cloudSyncPasswordHashJobId
        }
      })
    },
    async toggleTwoFactor () {
      return this.runIntegrationAction({
        actionName: 'toggle-totp',
        playbook: 'toggle-workspace-broker-totp',
        integration: this.$t('label.workspace.two.factor'),
        variables: {}
      })
    },
    confirmBrokerMigration () {
      this.$confirm({
        title: this.$t('label.workspace.broker.migrate.to.horizon.confirm'),
        content: this.$t('message.workspace.broker.migrate.to.horizon.confirm'),
        okText: this.$t('label.workspace.broker.migrate.to.horizon'),
        okType: 'danger',
        cancelText: this.$t('label.cancel'),
        onOk: () => this.runBrokerMigration('migrate-workspace-broker-smartrtc-to-horizon', 'migrate-broker-horizon')
      })
    },
    confirmBrokerMigrationToSmartRtc () {
      this.$confirm({
        title: this.$t('label.workspace.broker.migrate.to.smartrtc.confirm'),
        content: this.$t('message.workspace.broker.migrate.to.smartrtc.confirm'),
        okText: this.$t('label.workspace.broker.migrate.to.smartrtc'),
        okType: 'danger',
        cancelText: this.$t('label.cancel'),
        onOk: () => this.runBrokerMigration('migrate-workspace-broker-horizon-to-smartrtc', 'migrate-broker-smartrtc')
      })
    },
    async runBrokerMigration (playbook, actionName) {
      return this.runIntegrationAction({
        actionName,
        playbook,
        integration: this.$t('label.workspace.broker.migration'),
        variables: {}
      })
    },
    async runIntegrationAction ({ actionName, playbook, integration, variables }) {
      this.runningAction = actionName
      try {
        const params = await this.getAnsibleBaseParams(this.workspace)
        const configurationJson = await this.asyncApi('getWorkspaceConfiguration', {
          workspace_uuid: this.workspace.uuid
        })
        this.copyObject(configurationJson.workspaceconfigurationresponse.payload.configuration, params)
        Object.assign(params, variables)
        const task = await this.asyncApi('startAnsibleTaskAsync', {
          playbook,
          vars: this.encodeVars(this.convertNamespaces(params))
        })
        await this.waitForTask(
          task.ansiblestarttaskasyncresponse.jobid,
          this.$t('message.workspace.integration.started', { integration }),
          this.$t('message.workspace.integration.failed', { integration }),
          this.$t('message.workspace.integration.completed', { integration })
        )
        await this.load()
        return true
      } catch (error) {
        this.$notification.error({
          message: this.$t('label.error'),
          description: this.$t('message.workspace.integration.failed', { integration })
        })
        return false
      } finally {
        this.runningAction = null
      }
    }
  }
}
</script>

<style scoped>
.form-help {
  color: rgba(0, 0, 0, 0.55);
  font-size: 12px;
  margin-top: 4px;
}
.integration-help {
  margin-top: 16px;
}
.integration-actions {
  margin-top: 8px;
  text-align: right;
}
@media (max-width: 767px) {
  .integration-actions {
    text-align: left;
  }
}
</style>
